
import React, { useState, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";

import { MeasurementBasicFields } from "./form-fields/MeasurementBasicFields";
import { ProductCheckboxSelect } from "./form-fields/ProductCheckboxSelect";
import { FormActions } from "./form-fields/FormActions";
import { VariantMeasurementFormValues } from "@/hooks/useVariantMeasurements";
import { useFactories } from "@/hooks/useFactories";

const formSchema = z.object({
  part_name: z.string().min(1, "Part name is required"),
  measurement_value: z.number().min(0, "Measurement value must be positive"),
  tolerance: z.number().nullable().optional(),
  notes: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  products: z.array(z.string()).default([]),
  factories: z.array(z.string()).default([]).optional(),
  is_required: z.boolean().default(false),
  measurement_order: z.number().default(0)
});

export type VariantMeasurementFormSchema = z.infer<typeof formSchema>;

interface VariantMeasurementFormProps {
  defaultValues: VariantMeasurementFormValues;
  onSubmit: (values: VariantMeasurementFormValues) => Promise<void>;
  isSubmitting: boolean;
  isEditing: boolean;
  onCancel: () => void;
  availableProducts?: any[];
}

export function VariantMeasurementForm({
  defaultValues,
  onSubmit,
  isSubmitting,
  isEditing,
  onCancel,
  availableProducts = []
}: VariantMeasurementFormProps) {
  const { factories, loading: factoriesLoading } = useFactories();
  const form = useForm<VariantMeasurementFormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      part_name: defaultValues.part_name || "",
      measurement_value: defaultValues.measurement_value || 0,
      tolerance: defaultValues.tolerance || null,
      notes: defaultValues.notes || "",
      description: defaultValues.description || "",
      products: defaultValues.products || [],
      factories: defaultValues.factories || [],
      is_required: defaultValues.is_required || false,
      measurement_order: defaultValues.measurement_order || 0
    }
  });

  // Update form values when defaultValues change (for editing)
  React.useEffect(() => {
    if (defaultValues) {
      form.reset({
        part_name: defaultValues.part_name || "",
        measurement_value: defaultValues.measurement_value || 0,
        tolerance: defaultValues.tolerance || null,
        notes: defaultValues.notes || "",
        description: defaultValues.description || "",
        products: defaultValues.products || [],
        factories: defaultValues.factories || [],
        is_required: defaultValues.is_required || false,
        measurement_order: defaultValues.measurement_order || 0
      });
    }
  }, [defaultValues, form]);

  const handleSubmit = async (values: VariantMeasurementFormSchema) => {
    const formValues: VariantMeasurementFormValues = {
      part_name: values.part_name,
      measurement_value: values.measurement_value,
      tolerance: values.tolerance,
      notes: values.notes,
      description: values.description,
      products: values.products,
      factories: values.factories,
      is_required: values.is_required,
      measurement_order: values.measurement_order
    };
    
    await onSubmit(formValues);
  };

  const selectedFactories = form.watch("factories") || [];

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <MeasurementBasicFields form={form} />
        
        <ProductCheckboxSelect form={form} products={availableProducts} />
        
        <FormField
          control={form.control}
          name="factories"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Associated Factories</FormLabel>
              <FormControl>
                <div className="border rounded-md p-1">
                  <ScrollArea className="h-[150px]">
                    <div className="p-3 space-y-2">
                      {factoriesLoading ? (
                        <p className="text-sm text-muted-foreground">Loading factories...</p>
                      ) : factories.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No factories available</p>
                      ) : (
                        factories.map((factory) => (
                          <div key={factory.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`factory-${factory.id}`}
                              checked={selectedFactories.includes(factory.name)}
                              onCheckedChange={(checked) => {
                                const updatedFactories = checked
                                  ? [...selectedFactories, factory.name]
                                  : selectedFactories.filter(name => name !== factory.name);
                                form.setValue("factories", updatedFactories, {
                                  shouldValidate: true,
                                  shouldDirty: true
                                });
                              }}
                            />
                            <label
                              htmlFor={`factory-${factory.id}`}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              {factory.name}
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormActions
          form={form}
          isSubmitting={isSubmitting}
          isEditing={isEditing}
          onCancel={onCancel}
        />
      </form>
    </Form>
  );
}
